import { ItemEditor as CampfireItemEditor } from "@campfirelearning/sdk-lti";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import uuid from "uuid";

import { gql } from "@examspark/api";
import { exists, getBankIdFromData } from "@examspark/common";
import { Loading, useVolatileCallback } from "@examspark/common-ui";

import { useQueryListBanks } from "../../api/operations/bank";
import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useLtiParams } from "../activities/ReplaceActivityItem";

import { ItemEditorIframedProps } from "./ItemEditorIframedModal";

const StyledExamEditor = styled.div`
  height: 100%;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    box-sizing: border-box;
  }
`;

/** Component to use our own Campfire SDK ItemEditor component. */
const ItemEditorIframed = ({
  itemId,
  onSave,
  onClose,
  onChange,
  onInit,
  createWithTags,
  objectiveAuthorityIds,
}: ItemEditorIframedProps & {
  onClose?: () => void;
  onChange?: (modified: boolean) => void;
}) => {
  // const [isModified, setIsModified] = useState(false);
  const consumer = useRef<CampfireItemEditor | null>(null);
  const ltiParams = useLtiParams();
  const authorizationContext = useAuthorizationContext();
  const banksQuery = useQueryListBanks();

  const [ltiStatus, setLtiStatus] = React.useState<string | null>(null);

  const handleChange = useVolatileCallback(onChange);
  const handleClose = useVolatileCallback(onClose);
  const handleSave = useVolatileCallback(onSave);

  const domId = useRef<string>(uuid.v4());

  useEffect(() => {
    if (ltiParams == null) {
      console.info("Waiting for LTI params");
      return;
    }

    if (!banksQuery.data) {
      console.info("waiting for banks");
      return;
    }

    // ensure the bank id exists before trying to set it
    const bankId = `${authorizationContext.workspaceId}/user-content`;

    // console.log("auth context", authorizationContext);
    // console.log({ data: banksQuery.data, bankId });
    const foundBank = banksQuery.data
      .flatMap(({ items }) => items)
      .filter(exists)
      .find((bank) => getBankIdFromData(bank) === bankId);

    if (!consumer.current) {
      consumer.current = new CampfireItemEditor({
        id: itemId,
        params: ltiParams,
        launchOptions: {
          createWithTags: createWithTags,
          userPrefs: {
            objectiveAuthorityIds,
          },
        },
        domain:
          process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
        signingFunction:
          (launchUrl: string) => async (params: Record<string, string>) => {
            try {
              const signer = await gql.operations.app.signCampfireLti(
                launchUrl,
                params,
                authorizationContext,
              );

              return {
                ...params,
                oauth_signature: signer.data?.signCampfireLti.signature ?? "",
              };
            } catch (e) {
              console.error(e);
            }

            return params;
          },
      });

      onInit?.(consumer.current);
    }

    if (consumer.current.setDefaultItemMetadata) {
      console.log("setting default metadata");
      consumer.current.setDefaultItemMetadata([
        { field: "item:maturity", value: "OPERATIONAL" },
        { field: "item:visibility", value: "PRIVATE" },
        {
          field: "item:bank",
          value: foundBank ? bankId : "",
        },
      ]);
    }

    const listener = (event: MessageEvent) => {
      if (event.data.name === "launched") {
        setLtiStatus("launched");
      } else if (event.data.name === "itemHasUnsavedChanges") {
        console.log("here, ", event.data.data.modified);
        handleChange?.(event.data.data.modified);
      } else if (event.data.name === "itemSaved") {
        handleSave?.(event.data.data);
      } else if (event.data.name === "itemEditorClosed") {
        handleClose?.();
      }
    };
    consumer.current.addEventListener("message", listener);

    consumer.current.mount(domId.current).then(() => {
      console.log("Mounted!");
    });

    return () => {
      consumer.current?.removeEventListener("message", listener);
    };
  }, [
    authorizationContext,
    itemId,
    handleChange,
    handleClose,
    handleSave,
    ltiParams,
    banksQuery.data,
    onInit,
    createWithTags,
    objectiveAuthorityIds,
  ]);

  return (
    <StyledExamEditor>
      {ltiStatus !== "launched" && <Loading />}
      <iframe
        title="Item Editor"
        style={{ display: ltiStatus === "launched" ? "block" : "none" }}
        id={domId.current}
        name={domId.current}
      ></iframe>
    </StyledExamEditor>
  );
};

export default ItemEditorIframed;
